import { motion } from 'framer-motion';
import {
  Menu,
  X,
  CalendarCheck,
  User,
  LogOut,
  Settings,
  MessageSquare,
  Sparkles,
  Bot,
} from 'lucide-react';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { useAuth } from './Auth/AuthContext';
import NotificationBell from './NotificationBell';
import './Navbar.css';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isHost, isAdmin, profile, logout } = useAuth();

  // Handler for opening host upgrade flow
  const openHostUpgrade = () => {
    navigate('/become-host');
  };

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Check if we're on a page that needs different nav links
  const isAppPage = location.pathname !== '/';
  const isAnalyticsPage = location.pathname.startsWith('/events/analytics');
  const isAuthPage = location.pathname === '/login' || location.pathname === '/register';

  return (
    <motion.nav
      className="navbar"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="container">
        <div className="nav-content">
          <div className="logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <img
              src="/gatherpilot-logo.svg"
              alt="GatherPilot Logo"
              className="logo-icon"
              style={{ width: '48px', height: '48px' }}
            />
            <span className="logo-text">
              {isAnalyticsPage ? 'John Doe - Doe Hospitality Group' : 'GatherPilot'}
            </span>
          </div>

          <div className={`nav-links ${isOpen ? 'active' : ''}`}>
            {isAppPage ? (
              <>
                {/* Dynamic Navigation based on user type */}
                {user ? (
                  isHost || isAdmin || (profile?.is_host && profile?.host_status === 'approved') ? (
                    <>
                      {/* Host Navigation - Clean and Simple */}
                      <button
                        type="button"
                        onClick={() => navigate('/discover')}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: 'inherit',
                          color: 'inherit',
                          padding: '0',
                        }}
                      >
                        Discover
                      </button>

                      <button
                        type="button"
                        onClick={() => navigate('/my-events')}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: 'inherit',
                          color: 'inherit',
                          padding: '0',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <CalendarCheck size={16} style={{ marginRight: '4px' }} />
                        My Events
                      </button>

                      <button
                        type="button"
                        onClick={() => navigate('/host')}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: 'inherit',
                          color: 'inherit',
                          padding: '0',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <Bot size={16} style={{ marginRight: '4px' }} />
                        Host Dashboard
                      </button>
                    </>
                  ) : (
                    <>
                      {/* Regular User Dashboard Navigation */}
                      <button
                        type="button"
                        onClick={() => navigate('/discover')}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: 'inherit',
                          color: 'inherit',
                          padding: '0',
                        }}
                      >
                        Discover
                      </button>

                      <button
                        type="button"
                        onClick={() => navigate('/my-events')}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: 'inherit',
                          color: 'inherit',
                          padding: '0',
                        }}
                      >
                        My Events
                      </button>

                      <button
                        type="button"
                        className="become-host-btn"
                        onClick={openHostUpgrade}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: 'inherit',
                          color: 'inherit',
                          padding: '0',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <Sparkles size={16} style={{ marginRight: '4px' }} />
                        Become a Host
                        <span className="free-pill">Free Event</span>
                      </button>
                    </>
                  )
                ) : !isAuthPage ? (
                  <>
                    {/* Not logged in and not on auth pages - show login/register */}
                    <button
                      type="button"
                      onClick={() => navigate('/login')}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: 'inherit',
                        color: 'inherit',
                        padding: '0',
                      }}
                    >
                      Login
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('/register')}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: 'inherit',
                        color: 'inherit',
                        padding: '0',
                      }}
                    >
                      Sign Up
                    </button>
                  </>
                ) : null}
              </>
            ) : (
              <>
                {/* Homepage Navigation */}
                {user ? (
                  <>
                    {/* Show My Events for logged-in users even on homepage */}
                    <button
                      type="button"
                      onClick={() => navigate('/my-events')}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: 'inherit',
                        color: 'inherit',
                        padding: '0',
                      }}
                    >
                      My Events
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('/discover')}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: 'inherit',
                        color: 'inherit',
                        padding: '0',
                      }}
                    >
                      Discover
                    </button>
                    {!isHost && (
                      <button
                        type="button"
                        className="become-host-btn"
                        onClick={openHostUpgrade}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: 'inherit',
                          color: 'inherit',
                          padding: '0',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <Sparkles size={16} style={{ marginRight: '4px' }} />
                        Become a Host
                        <span className="free-pill">Free Event</span>
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    {/* Not logged in - show default homepage nav */}
                    <a href="#features">Features</a>
                    <a href="#events">Discover</a>
                    <button
                      type="button"
                      onClick={() => navigate('/become-a-host')}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: 'inherit',
                        color: 'inherit',
                        padding: '0',
                      }}
                    >
                      Become a Host
                    </button>
                  </>
                )}
              </>
            )}
          </div>

          <div className="nav-buttons">
            {isAppPage ? (
              <>
                <NotificationBell />
                {user && (
                  <div className="profile-menu-container" ref={profileMenuRef}>
                    <button
                      className="profile-btn"
                      onClick={() => setShowProfileMenu(!showProfileMenu)}
                    >
                      <div className="profile-avatar">
                        {profile?.avatar_url ? (
                          <img src={profile.avatar_url} alt="Profile" />
                        ) : (
                          <User size={20} />
                        )}
                      </div>
                      <span className="profile-name">
                        {profile?.full_name || user?.email?.split('@')[0] || 'User'}
                      </span>
                    </button>
                    {showProfileMenu && (
                      <div className="profile-dropdown">
                        <button
                          onClick={() => {
                            setShowProfileMenu(false);
                            navigate('/profile');
                          }}
                        >
                          <Settings size={16} />
                          Profile Settings
                        </button>
                        <button
                          onClick={() => {
                            setShowProfileMenu(false);
                            navigate('/messages');
                          }}
                        >
                          <MessageSquare size={16} />
                          Messages
                        </button>
                        <button
                          onClick={async () => {
                            setShowProfileMenu(false);
                            await logout();
                            navigate('/');
                          }}
                        >
                          <LogOut size={16} />
                          Logout
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="btn-secondary nav-btn ds-btn ds-btn-secondary"
                  onClick={() => {
                    navigate('/login');
                  }}
                >
                  Log In
                </button>
                <button
                  type="button"
                  className="btn-primary nav-btn ds-btn ds-btn-primary"
                  onClick={() => {
                    navigate('/register');
                  }}
                >
                  Get Started
                </button>
              </>
            )}

            <button className="mobile-menu-btn" onClick={() => setIsOpen(!isOpen)}>
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>
    </motion.nav>
  );
};

export default Navbar;
